import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../utils/api';
import { PageHeader, SearchBar, DataTable, Pagination, Loading, usePersistedState, BackButton } from '../components/Shared';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

// Étape 1 : saisie du nom pour générer l'ID
function ModalNom({ onConfirm, onClose }) {
  const [nom, setNom] = useState('');
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');

  function handleNomChange(val) {
    setNom(val);
    setError('');
    const prefix = val.trim().toUpperCase().replace(/[^A-Z]/g, '').substring(0, 3);
    if (prefix.length === 3) {
      setPreview(`ST_${prefix}XX (numéro attribué automatiquement)`);
    } else {
      setPreview('');
    }
  }

  function handleConfirm() {
    const prefix = nom.trim().toUpperCase().replace(/[^A-Z]/g, '').substring(0, 3);
    if (prefix.length < 3) {
      setError('Le nom doit contenir au moins 3 lettres');
      return;
    }
    onConfirm(nom.trim());
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '400px', maxWidth: '90vw' }}>
        <h3 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Nouveau stagiaire</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
          Entrez le nom du stagiaire pour générer son identifiant automatiquement.
        </p>

        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
          Nom complet *
        </label>
        <input
          type="text" value={nom} onChange={e => handleNomChange(e.target.value)}
          placeholder="Ex: MARTIN Pierre"
          autoFocus
          style={{ width: '100%', padding: '0.6rem 0.8rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box' }}
        />

        {preview && (
          <div style={{ marginTop: '0.6rem', padding: '0.5rem 0.8rem', background: 'rgba(59,130,246,0.1)', borderRadius: '6px', fontSize: '0.85rem', color: 'var(--accent-blue)' }}>
            ID généré : <strong>{preview}</strong>
          </div>
        )}
        {error && (
          <div style={{ marginTop: '0.6rem', padding: '0.5rem 0.8rem', background: 'rgba(239,68,68,0.1)', borderRadius: '6px', fontSize: '0.85rem', color: '#f87171' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleConfirm}>Suivant →</button>
        </div>
      </div>
    </div>
  );
}

// Étape 2 : saisie des informations générales
function ModalInfos({ nomInitial, onSave, onClose }) {
  const { token } = useAuth();
  const [form, setForm] = useState({
    civilite: '',
    nom: nomInitial || '',
    nom_jeune_fille: '',
    prenoms: '',
    client_id: '',
    email: '',
    tel: '',
    num_securite_sociale: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Charger la liste des clients pour le select
  const { data: clientsData } = useApi('/clients', { limit: 200 }, []);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/stagiaires`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onSave(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '460px', maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginBottom: '0.25rem', color: 'var(--text-primary)' }}>Informations générales</h3>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginBottom: '1.5rem' }}>
          L'ID sera généré automatiquement à l'enregistrement.
        </p>

        <div style={{ marginBottom: '0.8rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Civilité</label>
          <select value={form.civilite} onChange={e => setForm({ ...form, civilite: e.target.value })}
            style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
            <option value="">—</option>
            <option value="M.">M.</option>
            <option value="Mme">Mme</option>
          </select>
        </div>
        {[
          { label: 'Nom *', key: 'nom', type: 'text' },
          { label: 'Prénoms', key: 'prenoms', type: 'text' },
          { label: 'Nom de jeune fille', key: 'nom_jeune_fille', type: 'text' },
          { label: 'Email', key: 'email', type: 'email' },
          { label: 'Téléphone', key: 'tel', type: 'text' },
          { label: 'N° Sécurité sociale', key: 'num_securite_sociale', type: 'text' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: '0.8rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>{f.label}</label>
            <input
              type={f.type} value={form[f.key]}
              onChange={e => setForm({ ...form, [f.key]: e.target.value })}
              style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box' }}
            />
          </div>
        ))}

        <div style={{ marginBottom: '0.8rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Entreprise</label>
          <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })}
            style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
            <option value="">— Aucune —</option>
            {clientsData?.data?.map(c => (
              <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>
            ))}
          </select>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '0.5rem 0.8rem', marginBottom: '1rem', color: '#f87171', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : '✓ Créer le stagiaire'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Stagiaires() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [search, setSearch] = usePersistedState('stagiaires_search', '');
  const [page, setPage] = usePersistedState('stagiaires_page', 1);
  const [modal, setModal] = useState(null); // null | 'nom' | { nom }
  const [refresh, setRefresh] = useState(0);
  const [archives, setArchives] = usePersistedState('stagiaires_archives', false);
  const [clientId, setClientId] = usePersistedState('stagiaires_client', '');

  const { data: clientsData } = useApi('/clients', { limit: 500 }, []);
  const { data, loading } = useApi('/stagiaires', { search, page, limit: 25, archives: archives ? 'true' : undefined, client_id: clientId || undefined }, [search, page, refresh, archives, clientId]);

  const columns = [
    { header: 'ID', key: 'stagiaire_id', width: '120px', mobileHide: true },
    { header: 'Nom complet', render: r => <strong>{`${r.civilite||''} ${r.prenoms||''} ${r.nom||''}`.trim()}</strong> },
    { header: 'Client', render: r => r.nom_client || '—' },
    { header: 'Téléphone', render: r => r.tel || '—', mobileHide: true },
  ];

  function handleSaved(stagiaire) {
    setModal(null);
    setRefresh(r => r + 1);
    navigate(`/stagiaires/${stagiaire.stagiaire_id}`);
  }

  function handleRowClick(row) {
    const navState = {
      ids: data?.data?.map(r => r.stagiaire_id) || [],
      search,
      clientId,
      archives,
      page,
      total: data?.total || 0,
      limit: 25,
    };
    navigate(`/stagiaires/${row.stagiaire_id}`, { state: navState });
  }

  return (
    <div className="page">
      <PageHeader
        title="Stagiaires"
        subtitle={data ? `${data.total} stagiaires` : ''}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <BackButton />
            {hasRole('utilisateur') && <button className="btn-primary" onClick={() => setModal('nom')}>+ Nouveau stagiaire</button>}
          </div>
        }
      />

      <div className="toolbar">
        <SearchBar value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Rechercher un stagiaire..." />
        <div className="toolbar-filters">
          <select
            value={clientId}
            onChange={e => { setClientId(e.target.value); setPage(1); }}
            style={{ padding: '0.4rem 0.7rem', borderRadius: '20px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: clientId ? 'var(--text-primary)' : 'var(--text-secondary)', fontSize: '0.85rem', cursor: 'pointer', maxWidth: '200px' }}>
            <option value="">Tous les clients</option>
            {clientsData?.data?.map(c => (
              <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>
            ))}
          </select>
          <button className={`filter-chip ${archives ? 'active' : ''}`}
            onClick={() => { setArchives(a => !a); setPage(1); }}
            style={archives ? { borderColor: '#f87171', background: 'rgba(239,68,68,0.15)', color: '#f87171' } : {}}>
            🗄 Archivés
          </button>
        </div>
      </div>

      {loading ? <Loading /> : (
        <>
          <DataTable
            columns={columns}
            data={data?.data}
            onRowClick={handleRowClick}
          />
          <Pagination page={page} total={data?.total || 0} limit={25} onChange={setPage} />
        </>
      )}

      {modal === 'nom' && (
        <ModalNom
          onConfirm={nom => setModal({ nom })}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.nom && (
        <ModalInfos
          nomInitial={modal.nom}
          onSave={handleSaved}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
